import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import useSeo from '../../seo/useSeo';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import './ResetPassword.css';

// 비밀번호 재설정 페이지 (PG사 심사용 — 추후 제거 예정).
// 재설정 메일의 링크로 진입하면 supabase-js 가 URL 의 recovery 토큰을 파싱해
// 임시 세션을 만든다. 그 세션에서 updateUser 로 새 비밀번호를 저장한다.
export default function ResetPassword() {
  const { t } = useTranslation('resetPassword');
  // 메일 링크로만 진입하는 화면 — 색인 대상에서 제외
  useSeo({ page: 'resetPassword', path: '/reset-password', noindex: true });
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [hasSession, setHasSession] = useState(false);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;

    const params = new URLSearchParams(window.location.search);
    const tokenHash = params.get('token_hash');
    const type = params.get('type') || 'recovery';

    const init = async () => {
      // 1) token_hash 방식(권장) — 메일 링크가 우리 페이지로 오고, 검증은 여기 JS 에서 수행.
      //    Gmail 등의 링크 프리페치(순수 GET)로는 토큰이 소모되지 않아 'otp_expired' 를 피한다.
      if (tokenHash) {
        const { error } = await supabase.auth.verifyOtp({ type, token_hash: tokenHash });
        if (!mounted) return;
        setHasSession(!error);
        setChecking(false);
        return;
      }

      // 2) 구 implicit 방식(해시에 access_token) — supabase 가 자동 파싱한 세션 확인.
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!mounted) return;
      setHasSession(!!session);
      setChecking(false);
    };

    init();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;
      if (event === 'PASSWORD_RECOVERY' || session) {
        setHasSession(true);
        setChecking(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (password.length < 6) {
      setErrorMsg(t('errPasswordShort'));
      return;
    }
    if (password !== confirm) {
      setErrorMsg(t('errMismatch'));
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setDone(true);
    } catch (err) {
      console.error('비밀번호 변경 에러:', err.message);
      setErrorMsg(t('errGeneric'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="reset-page">
      <div className="reset-card">
        <h1 className="reset-title">
          Lansly<span className="logo-dot" aria-hidden="true" />
        </h1>

        {checking ? (
          <p className="reset-subtitle">{t('checking')}</p>
        ) : done ? (
          <>
            <p className="reset-subtitle">{t('doneMessage')}</p>
            <button className="reset-btn" onClick={() => navigate('/')}>
              {t('goHome')}
            </button>
          </>
        ) : !hasSession ? (
          <>
            <p className="reset-subtitle">{t('invalidLink')}</p>
            <button className="reset-btn" onClick={() => navigate('/')}>
              {t('goHome')}
            </button>
          </>
        ) : (
          <>
            <p className="reset-subtitle">{t('subtitle')}</p>
            <form className="reset-form" onSubmit={handleSubmit}>
              <input
                type="password"
                className="reset-input"
                placeholder={t('passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                disabled={submitting}
              />
              <input
                type="password"
                className="reset-input"
                placeholder={t('confirmPlaceholder')}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                disabled={submitting}
              />
              {errorMsg && <p className="reset-msg reset-msg--error">{errorMsg}</p>}
              <button type="submit" className="reset-btn" disabled={submitting}>
                {submitting ? t('submitting') : t('submit')}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
